import { CardDecorativeGradient } from '@mdvip/shared/components/blocks/CardDecorativeGradient';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Switch } from '@mdvip/shared/components/ui/switch';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { cn } from '@mdvip/shared/lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemberAutoPaySummary } from '@/hooks/payments/autoPay/useMemberAutoPaySummary';
import { AutoPayEnrollmentModal } from './AutoPayEnrollmentModal';
import { AddPaymentAccountModal } from './accounts/AddPaymentAccountModal';

/* Behind-content motif: a faint calendar watermark + scattered dots. Desktop-only (scoped by the
   card's decorationClassName), purely decorative. */
const DECORATION = (
  <>
    <Icon name="calendarDays" weight="solid" className="absolute -right-4 top-4 text-[116px] text-primary/15" />
    <span className="absolute right-7 top-6 h-2.5 w-2.5 rounded-full bg-primary/30" />
    <span className="absolute right-22 top-12 h-1.5 w-1.5 rounded-full bg-primary/40" />
    <span className="absolute right-10 top-21 h-2 w-2 rounded-full bg-primary/20" />
  </>
);

const CARD_CLASS = 'h-full gap-0 rounded-[24px] py-0!';

interface AutoPayCardProps {
  className?: string;
  /** Deep-link (`?turnOnAutopay=true`): opens the enrollment modal as soon as the card mounts. */
  autoOpenEnrollment?: boolean;
  /** Fired whenever enrollment is dismissed, so the host can drop the deep-link param. */
  onEnrollmentClose?: () => void;
}

/**
 * AutoPayCard: the Payment Center "AutoPay" status card. A compact icon + label, the status
 * ("Off"/"On") inline with a toggle, and a full-width benefit panel below. Off: a live toggle and a
 * tappable "Never miss a payment" panel, both of which open the enrollment modal. On: a green "On",
 * a disabled toggle, and a static "AutoPay is active" panel (management is handled by Member
 * Services). The toggle never enables AutoPay directly. It only opens the confirmation modal.
 * Status, amount, and plan come from {@link useMemberAutoPaySummary} (live member data).
 */
export function AutoPayCard({ className, autoOpenEnrollment = false, onEnrollmentClose }: AutoPayCardProps) {
  const { t } = useTranslation('members');
  const { data, isLoading } = useMemberAutoPaySummary();
  const [modalOpen, setModalOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const on = data?.autoPayEnrolled ?? false;
  const phone = MDVIP_COMPANY_INFO.memberSupportPhone;

  // Deep-link open. Keyed on the flag itself rather than mount so that clearing the URL param on
  // close (which flips this back to false) can't re-trigger it. An already-enrolled member still
  // opens the modal, which shows its "already on" state without firing a mutation.
  useEffect(() => {
    if (autoOpenEnrollment) setModalOpen(true);
  }, [autoOpenEnrollment]);

  const handleEnrollmentOpenChange = (open: boolean) => {
    setModalOpen(open);
    if (!open) onEnrollmentClose?.();
  };

  if (isLoading) {
    return (
      <CardDecorativeGradient
        tone="primary"
        data-slot="card-auto-pay"
        data-state="loading"
        className={cn(CARD_CLASS, className)}
      >
        <div className="flex flex-1 flex-col p-6" aria-busy="true">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
            <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="h-8 w-16 animate-pulse rounded bg-muted" />
            <div className="h-6 w-12 animate-pulse rounded-full bg-muted" />
          </div>
          <div className="mt-4 flex flex-1 flex-col justify-end">
            <div className="h-[68px] w-full animate-pulse rounded-2xl bg-muted" />
          </div>
        </div>
      </CardDecorativeGradient>
    );
  }

  const benefit = (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl px-4 py-3.5 text-left',
        on ? 'bg-success' : 'bg-tint-brand-soft',
      )}
    >
      {!on && (
        <span className="shrink-0 text-primary">
          <Icon name="shieldCheck" weight="solid" className="text-[20px]" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cn('block text-sm font-bold', on ? 'text-success-foreground' : 'text-foreground')}>
          {on ? t('payments.autopay.card.onTitle') : t('payments.autopay.card.offTitle')}
        </span>
        <span className="block text-xs text-muted-foreground">
          {on ? t('payments.autopay.card.onBody', { phone }) : t('payments.autopay.card.offBody')}
        </span>
      </span>
      {!on && (
        <Icon
          name="chevronRight"
          className="shrink-0 text-[13px] text-primary transition-transform group-hover:translate-x-0.5"
        />
      )}
    </div>
  );

  return (
    <>
      <CardDecorativeGradient
        tone="primary"
        data-slot="card-auto-pay"
        data-state={on ? 'on' : 'off'}
        decoration={DECORATION}
        decorationClassName="hidden sm:block"
        className={cn(CARD_CLASS, 'transition-shadow duration-200 hover:shadow-md', className)}
      >
        <div className="flex flex-1 flex-col p-6">
          {/* Header: compact icon + label */}
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-tint-brand text-primary">
              <Icon name="refreshCw" weight="solid" size="sm" />
            </span>
            <span className="text-base font-semibold text-foreground">{t('payments.autopay.label')}</span>
          </div>

          {/* Status: inline with the toggle (44px tap target) */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div
              className={cn('text-3xl font-semibold leading-none', on ? 'text-success-foreground' : 'text-foreground')}
            >
              {on ? t('payments.autopay.statusOn') : t('payments.autopay.statusOff')}
            </div>
            <span className="flex h-11 items-center pr-0.5">
              <span className="origin-right scale-125">
                <Switch
                  checked={on}
                  onCheckedChange={() => {
                    if (!on) setModalOpen(true);
                  }}
                  disabled={on}
                  aria-label={t('payments.autopay.toggleLabel')}
                />
              </span>
            </span>
          </div>

          {/* Benefit panel: tappable CTA when off, static confirmation when on. Grows + bottom-anchors. */}
          <div className="mt-4 flex flex-1 flex-col justify-end">
            {on ? (
              benefit
            ) : (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                aria-label={t('payments.autopay.toggleLabel')}
                className="group block w-full rounded-2xl transition-[box-shadow,background-color] hover:[&>div]:bg-tint-brand active:[&>div]:bg-tint-brand-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                {benefit}
              </button>
            )}
          </div>
        </div>
      </CardDecorativeGradient>

      <AutoPayEnrollmentModal
        open={modalOpen}
        onOpenChange={handleEnrollmentOpenChange}
        onAddAccount={() => {
          setModalOpen(false);
          setAddOpen(true);
        }}
      />

      {/* Add-account flow reached from the enrollment picker / empty state. Only one dialog is
          visible at a time; back and success both return to enrollment (the new card lands in the
          picker via the saved-methods cache invalidation). */}
      <AddPaymentAccountModal
        open={addOpen}
        onOpenChange={setAddOpen}
        fromAutopay
        onBackToAutopay={() => {
          setAddOpen(false);
          setModalOpen(true);
        }}
        onSuccess={() => {
          setAddOpen(false);
          setModalOpen(true);
        }}
      />
    </>
  );
}
